import { AxiosError } from 'axios';
import { t } from 'i18next';
import { useState } from 'react';
import { useTheme } from 'styled-components';

import Input from '../../../../../../components/Input';
import Modal from '../../../../../../components/Modal';
import {
  backendErrorNotification,
  frontendNotification,
} from '../../../../../../components/Notification';
import HttpStatus from '../../../../../../enums/httpStatus';
import { generateVisitScheduledFromRoutinesAPI } from '../../../../../../services/requests';

type Props = {
  closeModal: () => void;
  callback: () => void;
  target: {
    all_sellers: boolean;
    CodigoVendedor?: string;
    Nome?: string;
  };
};

const MAX_VISIT_SCHEDULE_QUANTITY = 15;
const MAX_VISIT_SCHEDULE_GENERATION_DAYS = 45;
const SMARTSELL_WEB_ORIGIN = 'SMARTSELL_WEB';

const ModalGenerateScheduledVisits = ({ closeModal, callback, target }: Props) => {
  const theme = useTheme();
  const [isLoading, setIsLoading] = useState(false);
  const [quantity, setQuantity] = useState(String(MAX_VISIT_SCHEDULE_QUANTITY));

  const parsedQuantity = Number(quantity);
  const isQuantityValid = Number.isInteger(parsedQuantity)
    && parsedQuantity >= 1
    && parsedQuantity <= MAX_VISIT_SCHEDULE_QUANTITY;

  const handleGenerate = async () => {
    if (!isQuantityValid) {
      frontendNotification({
        message: t('visitScheduleGenerationQuantityError'),
        type: 'error',
      });

      return;
    }

    setIsLoading(true);

    try {
      const payload = target.all_sellers
        ? { all_sellers: true, quantity: parsedQuantity, origin: SMARTSELL_WEB_ORIGIN }
        : {
          CodigoVendedor: target.CodigoVendedor,
          quantity: parsedQuantity, origin: SMARTSELL_WEB_ORIGIN
        };

      const res = await generateVisitScheduledFromRoutinesAPI.post(payload);

      if (res.status !== HttpStatus.OK) {
        throw res;
      }

      frontendNotification({
        message: t('visitScheduleGenerationSuccess'),
        type: 'success',
      });

      callback();
      closeModal();
    } catch (err) {
      if (!generateVisitScheduledFromRoutinesAPI.axios.isCancel(err)) {
        backendErrorNotification(err as AxiosError<any, any>);
      }
    }

    setIsLoading(false);
  };

  const description = target.all_sellers
    ? t('visitScheduleGenerationAllDescription')
    : t('visitScheduleGenerationSellerDescription', {
      sellerCode: target.CodigoVendedor,
      sellerName: target.Nome,
    });

  return (
    <Modal
      title={t('generateVisitSchedules')}
      action={handleGenerate}
      showModal
      closeModal={closeModal}
      cancelButton={closeModal}
      actionNameBtn={t('confirm')}
      isLoading={isLoading}
      style={{ width: 'min(80vw, 480px)' }}
      bodyStyle={{ paddingLeft: '1rem', paddingRight: '1rem' }}
    >
      <div style={{ padding: '1rem', display: 'grid', gap: '1rem' }}>
        <div>{description as string}</div>

        <Input
          label={t('quantity') as string}
          type="number"
          value={quantity}
          min={1}
          max={MAX_VISIT_SCHEDULE_QUANTITY}
          onChange={(event) => setQuantity(event.target.value.replace(/[^0-9]/g, ''))}
          validator={(value) => {
            if (!value) {
              return false;
            }

            const numericValue = Number(value);

            return Number.isInteger(numericValue)
              && numericValue >= 1
              && numericValue <= MAX_VISIT_SCHEDULE_QUANTITY;
          }}
          errorMsg={t('visitScheduleGenerationQuantityError', {
            maxQuantity: MAX_VISIT_SCHEDULE_QUANTITY,
          }) as string}
        />

        <small style={{ color: theme.colors.negative, fontWeight: 600 }}>
          {t('visitScheduleGenerationQuantityHelper', {
            maxQuantity: MAX_VISIT_SCHEDULE_QUANTITY,
            maxDays: MAX_VISIT_SCHEDULE_GENERATION_DAYS,
          }) as string}
        </small>
      </div>
    </Modal>
  );
};

export default ModalGenerateScheduledVisits;
